import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings; accept
// either string or number (matches AR / loan / prepaid / accrual conventions).
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
const DecimalLikeNonNeg = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const FxRateSourceSchema = z.enum(['BOT', 'MANUAL', 'YAHOO']);
export const FxRevaluationStatusSchema = z.enum(['DRAFT', 'POSTED', 'REVERSED']);
// ============================================================
// FxExchangeRate — point-in-time mid rate row
// ============================================================
const CurrencyCodeSchema = z
    .string()
    .length(3, 'currency must be a 3-letter ISO 4217 code')
    .regex(/^[A-Z]{3}$/, 'currency must be uppercase 3-letter code');
export const FxExchangeRateSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    currency: CurrencyCodeSchema,
    rateDate: z.coerce.date(),
    midRate: DecimalLikeNonNeg,
    source: FxRateSourceSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// POST /fx/rates body — register a rate (typically end-of-month BOT mid).
export const CreateFxExchangeRateSchema = z.object({
    currency: CurrencyCodeSchema,
    rateDate: z.iso.date(),
    midRate: z.coerce.number().positive(),
    source: FxRateSourceSchema.default('BOT'),
});
// ============================================================
// FxRevaluationLine — detail row
// ============================================================
export const FxRevaluationLineSchema = z.object({
    id: z.uuid(),
    revaluationId: z.uuid(),
    accountCode: z.string().min(1),
    accountName: z.string(),
    foreignAmount: DecimalLike,
    thbBefore: DecimalLike,
    thbAfter: DecimalLike,
    gainLoss: DecimalLike,
    sourceTable: z.string(),
    sourceId: z.string(),
    createdAt: z.coerce.date(),
});
// ============================================================
// FxRevaluation — header row
// ============================================================
export const FxRevaluationSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    currency: CurrencyCodeSchema,
    rateUsed: DecimalLikeNonNeg,
    totalForeign: DecimalLike,
    totalThbBefore: DecimalLike,
    totalThbAfter: DecimalLike,
    unrealizedGainLoss: DecimalLike,
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    status: FxRevaluationStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    reversalJournalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    reversedBy: z.string().nullable().optional(),
    reversedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Detail view — header + lines, served by GET /fx-revaluations/:id.
export const FxRevaluationWithLinesSchema = FxRevaluationSchema.extend({
    lines: z.array(FxRevaluationLineSchema),
});
// List-row — minimal extras for the period/currency grid view.
export const FxRevaluationListRowSchema = FxRevaluationSchema.extend({
    lineCount: z.number().int().nonnegative().optional(),
});
// ============================================================
// POST /fx-revaluations/run — kick a period
// ============================================================
export const RunFxRevaluationSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const RunFxRevaluationResultSchema = z.object({
    period: z.string(),
    currenciesProcessed: z.number().int().nonnegative(),
    balancesEvaluated: z.number().int().nonnegative(),
    revaluationsCreated: z.number().int().nonnegative(),
    revaluationsSkipped: z.number().int().nonnegative(),
    totalGain: DecimalLike,
    totalLoss: DecimalLike,
    netGainLoss: DecimalLike,
    // One entry per currency processed — empty when no foreign balances exist.
    revaluations: z.array(z.object({
        currency: z.string(),
        revaluationId: z.uuid(),
        journalEntryId: z.uuid().nullable(),
        journalVoucherNo: z.string().nullable(),
        unrealizedGainLoss: DecimalLike,
    })),
});
// ============================================================
// POST /fx-revaluations/reverse — reverse a POSTED period
// ============================================================
export const ReverseFxRevaluationSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const ReverseFxRevaluationResultSchema = z.object({
    period: z.string(),
    revaluationsReversed: z.number().int().nonnegative(),
    revaluationsSkipped: z.number().int().nonnegative(),
    totalReversedAmount: DecimalLike,
    reversals: z.array(z.object({
        currency: z.string(),
        revaluationId: z.uuid(),
        reversalJournalEntryId: z.uuid().nullable(),
        reversalVoucherNo: z.string().nullable(),
    })),
});
//# sourceMappingURL=fx-revaluation.js.map